import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { TenantsAPI, InstitutionsAPI, YearsAPI, ClassesAPI } from "@/lib/sprint2-api";
import { FeesAPI } from "@/lib/sprint6-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Loader2, Wallet, Receipt, Send, X, Trash2, BarChart3, Printer, AlertTriangle, TrendingUp } from "lucide-react";

const STATUS_PILL = {
  draft: "bg-zinc-500/10 text-zinc-600 dark:text-zinc-300 border-zinc-500/20",
  issued: "bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/20",
  open: "bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/20",
  partial: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20",
  paid: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20",
  void: "bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/20",
  overdue: "bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/20",
};

const fmtMoney = (a, c = "INR") => `${c} ${Number(a || 0).toLocaleString("en-IN")}`;

export default function Fees() {
  const { user } = useAuth();
  const isSuper = user?.roles?.includes("super_admin");
  const isParent = user?.roles?.length === 1 && user.roles[0] === "parent";
  const queryClient = useQueryClient();

  const [tenantId, setTenantId] = useState(isSuper ? "" : user?.tenant_id || "");
  const tenantsQuery = useQuery({ queryKey: ["tenants"], queryFn: () => TenantsAPI.list(), enabled: isSuper });
  useEffect(() => { if (isSuper && !tenantId && tenantsQuery.data?.length) setTenantId(tenantsQuery.data[0].id); }, [isSuper, tenantId, tenantsQuery.data]);
  const tParam = isSuper && tenantId ? { tenant_id: tenantId } : undefined;
  const effective = isSuper ? tenantId : user?.tenant_id;

  // Parent view: only invoices for their children
  if (isParent) return <ParentInvoices />;

  return (
    <AppShell>
      <div className="p-8" data-testid="fees-page">
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
<h1 className="font-display font-extrabold tracking-tight text-4xl">Fees</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Fee structures, invoices, and payments. Live Razorpay/Stripe wires
              in the moment you share keys — until then, payments are recorded
              manually (cash/UPI/cheque/online ref).
            </p>
          </div>
          {isSuper && (
            <div className="space-y-1.5 min-w-[180px]">
              <Label className="text-[10px] font-semibold uppercase tracking-wider">Tenant</Label>
              <Select value={tenantId} onValueChange={setTenantId}>
                <SelectTrigger data-testid="fees-tenant"><SelectValue /></SelectTrigger>
                <SelectContent>{tenantsQuery.data?.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </div>

        <Tabs defaultValue="invoices">
          <TabsList>
            <TabsTrigger value="invoices" data-testid="tab-invoices"><Receipt className="h-3.5 w-3.5 mr-1.5" />Invoices</TabsTrigger>
            <TabsTrigger value="structures" data-testid="tab-structures"><Wallet className="h-3.5 w-3.5 mr-1.5" />Structures</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports"><BarChart3 className="h-3.5 w-3.5 mr-1.5" />Reports</TabsTrigger>
          </TabsList>
          <TabsContent value="invoices" className="mt-6"><InvoicesPanel tenantParam={tParam} effective={effective} /></TabsContent>
          <TabsContent value="structures" className="mt-6"><StructuresPanel tenantParam={tParam} effective={effective} /></TabsContent>
          <TabsContent value="reports" className="mt-6"><ReportsPanel tenantParam={tParam} /></TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}

function StructuresPanel({ tenantParam, effective }) {
  const queryClient = useQueryClient();
  const instQ = useQuery({ queryKey: ["institutions", effective], queryFn: () => InstitutionsAPI.list(tenantParam), enabled: !!effective });
  const [instId, setInstId] = useState("");
  useEffect(() => { if (!instId && instQ.data?.length) setInstId(instQ.data[0].id); }, [instId, instQ.data]);
  const yearsQ = useQuery({ queryKey: ["years", instId], queryFn: () => YearsAPI.list({ institution_id: instId, ...(tenantParam || {}) }), enabled: !!instId });
  const yearId = yearsQ.data?.find(y => y.is_current)?.id || yearsQ.data?.[0]?.id;
  const classesQ = useQuery({ queryKey: ["classes", yearId], queryFn: () => ClassesAPI.list({ academic_year_id: yearId, ...(tenantParam || {}) }), enabled: !!yearId });
  const structQ = useQuery({
    queryKey: ["structures", instId, yearId],
    queryFn: () => FeesAPI.listStructures({ institution_id: instId, academic_year_id: yearId, ...(tenantParam || {}) }),
    enabled: !!yearId,
  });

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-end flex-wrap">
        <div className="space-y-1.5 min-w-[200px]">
          <Label className="text-[10px] font-semibold uppercase tracking-wider">Institution</Label>
          <Select value={instId} onValueChange={setInstId}>
            <SelectTrigger data-testid="struct-institution"><SelectValue /></SelectTrigger>
            <SelectContent>{instQ.data?.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <NewStructureDialog yearId={yearId} instId={instId} classes={classesQ.data || []} onDone={() => queryClient.invalidateQueries({ queryKey: ["structures"] })} />
      </div>

      <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Class</TableHead><TableHead>Frequency</TableHead><TableHead>Heads</TableHead><TableHead>Total</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {structQ.isLoading ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline" /></TableCell></TableRow>
              : !structQ.data?.length ? <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">No structures yet</TableCell></TableRow>
              : structQ.data.map(s => {
                const cls = classesQ.data?.find(c => c.id === s.class_id);
                return (
                  <TableRow key={s.id} data-testid={`struct-row-${s.id}`}>
                    <TableCell><div className="font-medium">{s.name}</div></TableCell>
                    <TableCell className="text-sm">{cls?.name || <span className="text-muted-foreground">all</span>}</TableCell>
                    <TableCell className="text-xs capitalize">{s.frequency}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{s.heads.length} heads</TableCell>
                    <TableCell className="font-mono text-sm">{fmtMoney(s.total, s.currency)}</TableCell>
                    <TableCell className="text-right">
                      <IssueDialog structure={s} onDone={() => queryClient.invalidateQueries({ queryKey: ["invoices"] })} />
                      <Button size="icon" variant="ghost" onClick={async () => {
                        if (!window.confirm("Deactivate this structure?")) return;
                        try { await FeesAPI.removeStructure(s.id); toast.success("Removed"); queryClient.invalidateQueries({ queryKey: ["structures"] }); }
                        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                      }} data-testid={`struct-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function NewStructureDialog({ yearId, instId, classes, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", frequency: "annual", class_id: "" });
  const [heads, setHeads] = useState([{ key: "tuition", label: "Tuition Fee", amount: "" }]);
  const [busy, setBusy] = useState(false);
  const updateHead = (i, k, v) => setHeads(h => h.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const addHead = () => setHeads(h => [...h, { key: "", label: "", amount: "" }]);
  const removeHead = (i) => setHeads(h => h.filter((_, idx) => idx !== i));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await FeesAPI.createStructure({
        institution_id: instId, academic_year_id: yearId,
        class_id: form.class_id || undefined, name: form.name, frequency: form.frequency,
        heads: heads.filter(h => h.label && h.amount).map(h => ({ key: h.key || h.label.toLowerCase().replace(/\s+/g, "_"), label: h.label, amount: Number(h.amount) })),
      });
      toast.success("Structure created"); setOpen(false);
      setForm({ name: "", frequency: "annual", class_id: "" }); setHeads([{ key: "tuition", label: "Tuition Fee", amount: "" }]);
      onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button disabled={!yearId} data-testid="create-structure-button"><Plus className="h-4 w-4 mr-1.5" />Structure</Button></DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>New fee structure</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-wider">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="struct-form-name" /></div>
            <div className="space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-wider">Frequency</Label>
              <Select value={form.frequency} onValueChange={v => setForm({ ...form, frequency: v })}>
                <SelectTrigger data-testid="struct-form-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>{["annual", "quarterly", "monthly", "one_time"].map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-wider">Applies to class (optional)</Label>
            <Select value={form.class_id} onValueChange={v => setForm({ ...form, class_id: v })}>
              <SelectTrigger data-testid="struct-form-class"><SelectValue placeholder="All classes" /></SelectTrigger>
              <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2"><Label className="text-xs font-semibold uppercase tracking-wider">Heads</Label><Button type="button" size="sm" variant="outline" onClick={addHead} data-testid="add-head-button"><Plus className="h-3 w-3 mr-1" />Head</Button></div>
            <div className="space-y-2" data-testid="heads-list">
              {heads.map((h, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                  <Input placeholder="Label" value={h.label} onChange={(e) => updateHead(i, "label", e.target.value)} data-testid={`head-label-${i}`} />
                  <Input type="number" placeholder="Amount" value={h.amount} onChange={(e) => updateHead(i, "amount", e.target.value)} data-testid={`head-amount-${i}`} />
                  <Button type="button" size="icon" variant="ghost" onClick={() => removeHead(i)} disabled={heads.length === 1}><X className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
            </div>
            <div className="text-right text-sm font-mono mt-2">Total: ₹ {heads.reduce((a, h) => a + (Number(h.amount) || 0), 0).toLocaleString("en-IN")}</div>
          </div>
          <DialogFooter><Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button type="submit" disabled={busy} data-testid="struct-form-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Create</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function IssueDialog({ structure, onDone }) {
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [dueDate, setDueDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const out = await FeesAPI.issue(structure.id, { structure_id: structure.id, period, due_date: dueDate || undefined });
      setResult(out); onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setResult(null); }}>
      <DialogTrigger asChild><Button size="sm" variant="ghost" data-testid={`issue-button-${structure.id}`}><Send className="h-3.5 w-3.5 mr-1" />Issue</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Issue invoices — {structure.name}</DialogTitle></DialogHeader>
        {!result ? (
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-wider">Period</Label><Input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="2026-02" required data-testid="issue-period" /></div>
            <div className="space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-wider">Due date</Label><Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="issue-due" /></div>
            <DialogFooter><Button type="submit" disabled={busy} data-testid="issue-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Issue to {structure.class_id ? "class" : "all students"}</Button></DialogFooter>
          </form>
        ) : (
          <div className="space-y-3" data-testid="issue-result">
            <div className="text-sm">Issued <b>{result.issued}</b> invoice(s) · skipped {result.skipped_existing} existing.</div>
            <div className="text-xs text-muted-foreground font-mono">Amount per invoice: {fmtMoney(result.total_amount_per_invoice)}</div>
            <DialogFooter><Button onClick={() => setOpen(false)}>Close</Button></DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function InvoicesPanel({ tenantParam, effective }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState("all");
  const invQ = useQuery({
    queryKey: ["invoices", effective, status],
    queryFn: () => FeesAPI.listInvoices({ ...(tenantParam || {}), ...(status !== "all" ? { status } : {}) }),
    enabled: !!effective,
  });
  const [paying, setPaying] = useState(null);
  const summary = invQ.data?.summary || {};

  const openReceipt = async (iid) => {
    try {
      const html = await FeesAPI.receiptHtml(iid);
      const blob = new Blob([html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load receipt");
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="invoice-summary">
        <Kpi label="Billed" value={fmtMoney(summary.total_billed)} />
        <Kpi label="Collected" value={fmtMoney(summary.total_paid)} tone="success" />
        <Kpi label="Outstanding" value={fmtMoney(summary.total_outstanding)} tone="warn" />
        <Kpi label="Invoices" value={Object.values(summary.count_by_status || {}).reduce((a, b) => a + b, 0)} />
      </div>
      <div className="flex gap-3 items-end">
        <div className="space-y-1.5 min-w-[140px]"><Label className="text-[10px] font-semibold uppercase tracking-wider">Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="invoice-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{["all", "issued", "open", "partial", "paid", "void"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Period</TableHead><TableHead>Student</TableHead><TableHead>Total</TableHead><TableHead>Paid</TableHead><TableHead>Balance</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {invQ.isLoading ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline" /></TableCell></TableRow>
              : !invQ.data?.invoices?.length ? <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground" data-testid="invoices-empty">No invoices yet — issue from a structure.</TableCell></TableRow>
              : invQ.data.invoices.map(inv => (
                <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                  <TableCell className="font-mono text-xs">{inv.period}</TableCell>
                  <TableCell className="text-sm">
                    {inv.student_name || <span className="font-mono text-muted-foreground">{inv.student_id.slice(-8)}</span>}
                  </TableCell>
                  <TableCell className="font-mono text-sm">{fmtMoney(inv.total, inv.currency)}</TableCell>
                  <TableCell className="font-mono text-sm">{fmtMoney(inv.paid_amount, inv.currency)}</TableCell>
                  <TableCell className="font-mono text-sm">{fmtMoney(inv.balance, inv.currency)}</TableCell>
                  <TableCell><Badge className={(STATUS_PILL[inv.status] || "") + " border capitalize"}>{inv.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    {["issued", "partial", "open"].includes(inv.status) && <Button size="sm" variant="outline" onClick={() => setPaying(inv)} data-testid={`pay-${inv.id}`}>Record payment</Button>}
                    {["paid", "partial"].includes(inv.status) && (
                      <Button size="icon" variant="ghost" title="Print receipt" onClick={() => openReceipt(inv.id)} data-testid={`receipt-${inv.id}`}><Printer className="h-3.5 w-3.5" /></Button>
                    )}
                    {inv.status !== "paid" && inv.status !== "void" && (
                      <Button size="icon" variant="ghost" onClick={async () => {
                        if (!window.confirm("Void this invoice?")) return;
                        try { await FeesAPI.voidInvoice(inv.id); toast.success("Voided"); queryClient.invalidateQueries({ queryKey: ["invoices"] }); }
                        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                      }} data-testid={`void-${inv.id}`}><X className="h-3.5 w-3.5" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
      {paying && <PayDialog invoice={paying} onClose={() => { setPaying(null); queryClient.invalidateQueries({ queryKey: ["invoices"] }); }} />}
    </div>
  );
}

function PayDialog({ invoice, onClose }) {
  const [amount, setAmount] = useState(invoice.balance);
  const [method, setMethod] = useState("cash");
  const [txnRef, setTxnRef] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await FeesAPI.recordPayment(invoice.id, { amount: Number(amount), method, txn_ref: txnRef || undefined });
      toast.success("Payment recorded", {
        action: {
          label: "Print receipt",
          onClick: async () => {
            try {
              const html = await FeesAPI.receiptHtml(invoice.id);
              const blob = new Blob([html], { type: "text/html" });
              const url = URL.createObjectURL(blob);
              window.open(url, "_blank", "noopener,noreferrer");
              setTimeout(() => URL.revokeObjectURL(url), 30000);
            } catch { /* ignore */ }
          },
        },
      });
      onClose();
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="pay-dialog">
        <DialogHeader><DialogTitle>Record payment</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-sm">Balance: <b className="font-mono">{fmtMoney(invoice.balance, invoice.currency)}</b></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-wider">Amount</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} max={invoice.balance} data-testid="pay-amount" /></div>
            <div className="space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-wider">Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
                <SelectContent>{["cash", "upi", "cheque", "online", "other"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-wider">Transaction ref</Label><Input value={txnRef} onChange={(e) => setTxnRef(e.target.value)} className="font-mono" data-testid="pay-txn-ref" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} disabled={busy || !amount} data-testid="pay-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Record</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ParentInvoices() {
  const invQ = useQuery({ queryKey: ["parent-invoices"], queryFn: () => FeesAPI.listInvoices() });
  return (
    <AppShell>
      <div className="p-8 max-w-4xl" data-testid="parent-fees-page">
        <h1 className="font-display font-extrabold tracking-tight text-4xl mb-2">My fees</h1>
        <p className="text-sm text-muted-foreground mb-6">Invoices for your linked children.</p>
        <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
          <Table>
            <TableHeader><TableRow><TableHead>Period</TableHead><TableHead>Items</TableHead><TableHead>Total</TableHead><TableHead>Balance</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {invQ.isLoading ? <TableRow><TableCell colSpan={5} className="text-center py-8"><Loader2 className="h-4 w-4 animate-spin inline" /></TableCell></TableRow>
                : !invQ.data?.invoices?.length ? <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">No invoices yet.</TableCell></TableRow>
                : invQ.data.invoices.map(inv => (
                  <TableRow key={inv.id}>
                    <TableCell className="font-mono">{inv.period}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{inv.line_items.map(li => li.label || li.head || li.name).join(", ")}</TableCell>
                    <TableCell className="font-mono">{fmtMoney(inv.total, inv.currency)}</TableCell>
                    <TableCell className="font-mono">{fmtMoney(inv.balance, inv.currency)}</TableCell>
                    <TableCell><Badge className={(STATUS_PILL[inv.status] || "") + " border capitalize"}>{inv.status}</Badge></TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
        <p className="text-xs text-muted-foreground mt-4">Online payment options will appear here once Razorpay/Stripe is wired (admin needs to share keys).</p>
      </div>
    </AppShell>
  );
}

function Kpi({ label, value, tone }) {
  const color = tone === "success" ? "text-emerald-600" : tone === "warn" ? "text-amber-600" : tone === "danger" ? "text-rose-600" : "";
  return (
    <div className="bg-card rounded-2xl shadow-sm p-4">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</div>
      <div className={`font-display font-extrabold text-2xl tracking-tight mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function ReportsPanel({ tenantParam }) {
  const rQ = useQuery({
    queryKey: ["fees-reports", tenantParam],
    queryFn: () => FeesAPI.reportsSummary(tenantParam || {}),
  });

  if (rQ.isLoading) {
    return <div className="flex items-center justify-center py-16 text-muted-foreground" data-testid="reports-loading"><Loader2 className="h-5 w-5 animate-spin mr-2" />Loading reports…</div>;
  }
  if (rQ.error) {
    return <div className="p-6 text-center text-destructive" data-testid="reports-error">Failed to load reports.</div>;
  }
  const d = rQ.data || { kpis: {}, defaulters: [], daily: [], per_head: [], class_wise: [] };
  const kpis = d.kpis || {};
  const perHeadMax = Math.max(1, ...(d.per_head || []).map(p => p.amount));
  const dailyMax = Math.max(1, ...(d.daily || []).map(x => x.amount));

  return (
    <div className="space-y-6" data-testid="reports-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total Collected" value={`₹ ${Number(kpis.total_collected || 0).toLocaleString("en-IN")}`} tone="success" />
        <Kpi label="Total Outstanding" value={`₹ ${Number(kpis.total_outstanding || 0).toLocaleString("en-IN")}`} tone="warn" />
        <Kpi label="Defaulters" value={kpis.defaulters_count || 0} tone="danger" />
        <Kpi label="Payments Received" value={kpis.total_payments || 0} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily collection chart */}
        <div className="bg-card rounded-2xl shadow-sm p-5" data-testid="daily-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-display font-bold text-lg">Daily Collection</div>
              <div className="text-xs text-muted-foreground">Last {d.daily?.length || 0} active day(s)</div>
            </div>
            <TrendingUp className="h-4 w-4 text-emerald-600" />
          </div>
          {d.daily?.length ? (
            <div className="flex items-end gap-1 h-40 border-b border-border">
              {d.daily.map((row) => (
                <div key={row.date} title={`${row.date} · ₹${Number(row.amount).toLocaleString("en-IN")} · ${row.count} payments`}
                     className="flex-1 bg-emerald-500/70 hover:bg-emerald-600 rounded-t transition-colors"
                     style={{ height: `${(row.amount / dailyMax) * 100}%`, minHeight: "2px" }} />
              ))}
            </div>
          ) : <div className="text-sm text-muted-foreground py-10 text-center">No payments in range.</div>}
          {d.daily?.length ? (
            <div className="flex justify-between text-[10px] font-mono text-muted-foreground mt-2">
              <span>{d.daily[0]?.date}</span>
              <span>{d.daily[d.daily.length - 1]?.date}</span>
            </div>
          ) : null}
        </div>

        {/* Per-head split */}
        <div className="bg-card rounded-2xl shadow-sm p-5" data-testid="per-head-chart">
          <div className="font-display font-bold text-lg mb-1">Collection by Head</div>
          <div className="text-xs text-muted-foreground mb-4">Weighted split of collected amount</div>
          {d.per_head?.length ? (
            <div className="space-y-2.5">
              {d.per_head.map(row => (
                <div key={row.head} data-testid={`head-${row.head}`}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-medium">{row.head}</span>
                    <span className="font-mono text-muted-foreground">₹ {Number(row.amount).toLocaleString("en-IN")}</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-sky-500 to-indigo-600 rounded-full" style={{ width: `${(row.amount / perHeadMax) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          ) : <div className="text-sm text-muted-foreground py-10 text-center">No collections yet.</div>}
        </div>
      </div>

      {/* Class-wise dues */}
      <div className="bg-card rounded-2xl shadow-sm overflow-hidden" data-testid="class-wise-table">
        <div className="p-5 pb-3">
          <div className="font-display font-bold text-lg">Class-wise Dues</div>
          <div className="text-xs text-muted-foreground">Outstanding balance by class (highest first)</div>
        </div>
        <Table>
          <TableHeader><TableRow><TableHead>Class</TableHead><TableHead className="text-right">Invoices</TableHead><TableHead className="text-right">Outstanding</TableHead></TableRow></TableHeader>
          <TableBody>
            {d.class_wise?.length ? d.class_wise.map(row => (
              <TableRow key={row.class_id} data-testid={`cw-${row.class_id}`}>
                <TableCell className="font-medium">{row.class_name || row.class_id.slice(-6)}</TableCell>
                <TableCell className="text-right font-mono text-sm">{row.invoices}</TableCell>
                <TableCell className="text-right font-mono text-sm">₹ {Number(row.outstanding).toLocaleString("en-IN")}</TableCell>
              </TableRow>
            )) : <TableRow><TableCell colSpan={3} className="text-center py-8 text-muted-foreground">No outstanding dues.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {/* Top defaulters */}
      <div className="bg-card rounded-2xl shadow-sm overflow-hidden" data-testid="defaulters-table">
        <div className="p-5 pb-3 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <div>
            <div className="font-display font-bold text-lg">Top Defaulters</div>
            <div className="text-xs text-muted-foreground">Students with overdue invoices (top {d.defaulters?.length || 0})</div>
          </div>
        </div>
        <Table>
          <TableHeader><TableRow><TableHead>Admission No</TableHead><TableHead>Student</TableHead><TableHead className="text-right">Invoices</TableHead><TableHead className="text-right">Outstanding</TableHead></TableRow></TableHeader>
          <TableBody>
            {d.defaulters?.length ? d.defaulters.slice(0, 25).map(row => (
              <TableRow key={row.student_id} data-testid={`def-${row.student_id}`}>
                <TableCell className="font-mono text-xs">{row.admission_no || "—"}</TableCell>
                <TableCell>{row.student_name || row.student_id.slice(-8)}</TableCell>
                <TableCell className="text-right font-mono text-sm">{row.invoices}</TableCell>
                <TableCell className="text-right font-mono text-sm text-amber-700 font-semibold">₹ {Number(row.outstanding).toLocaleString("en-IN")}</TableCell>
              </TableRow>
            )) : <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No defaulters found.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
